// Purpose: render an Agent Spec v1 builder where visual controls and JSON edit one contract object.
import {
  AlertTriangle,
  Braces,
  CheckCircle2,
  Code2,
  FileText,
  GitCompare,
  Loader2,
  Lock,
  PhoneCall,
  Plus,
  RotateCcw,
  Save,
  Settings,
  Sparkles,
  X,
} from "lucide-react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  clearTurnTakingOverrides,
  coerceValidAgentSpec,
  defaultAgentSpec,
  defaultTtsModelForProvider,
  defaultTtsVoiceForProvider,
  diffAgentSpecs,
  fieldKeyFromLabel,
  formatAgentSpec,
  getCaptureFields,
  getEndCallConfig,
  parseAgentSpec,
  preemptiveTtsEnabled,
  setCaptureFields,
  setEndCallConfig,
  setPreemptiveTtsEnabled,
  supportedTtsModelsForProvider,
  ttsVoiceSuggestionsForProvider,
  turnTakingOverrides,
  validateAgentSpec,
  type CallableAgentSpecV1,
  type CaptureFieldDefinition,
  type CaptureFieldType,
  type EndCallFinalMessageMode,
  type EndpointingMode,
  type AmbiencePreset,
  type FirstClauseBufferSettings,
  type FirstMessageMode,
  type LlmProvider,
  type SttProvider,
  type TtsProvider,
  type TtsSettings,
  type ValidationResult,
} from "./agentSpec";
import {
  fetchAgents,
  fetchAgentFolders,
  createAgent,
  createAgentFolder,
  updateAgentFolder,
  deleteAgentFolder,
  assignAgentFolder,
  fetchAgentWithVersions,
  fetchAgentVersionToolAttachments,
  createAgentVersion,
  publishAgentVersion,
  fetchTtsVoices,
  syncTtsVoices,
  updateAgentVersionMcpPermissions,
  ConsoleApiError,
  isUnauthorizedApiError,
  type ApiAgent,
  type ApiAgentFolder,
  type ApiAgentVersion,
  type ApiAgentVersionToolAttachment,
  type ApiMcpPermissionTool,
  type ApiAgentWithVersions,
  type ApiTtsVoice,
} from "./apiClient";
import { TestCallPanel } from "./TestCallPanel";

export function AgentBuilderScreen(props: { onSessionExpired: () => void }) {
  const { onSessionExpired } = props;
  const [spec, setSpec] = useState<CallableAgentSpecV1>(defaultAgentSpec);
  const [jsonText, setJsonText] = useState(formatAgentSpec(defaultAgentSpec));
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [validation, setValidation] = useState<ValidationResult>(
    validateAgentSpec(defaultAgentSpec),
  );

  const [agents, setAgents] = useState<ApiAgent[]>([]);
  const [folders, setFolders] = useState<ApiAgentFolder[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string>("all");
  const [newFolderName, setNewFolderName] = useState<string>("");
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [currentAgentWithVersions, setCurrentAgentWithVersions] =
    useState<ApiAgentWithVersions | null>(null);
  const detailRequestId = useRef(0);
  const selectedAgentIdRef = useRef<string | null>(null);
  const selectionGeneration = useRef(0);
  const detailReady = Boolean(selectedAgentId && currentAgentWithVersions?.agent.id === selectedAgentId);
  const editorAvailable = !selectedAgentId || detailReady;
  const [toolAttachments, setToolAttachments] = useState<
    ApiAgentVersionToolAttachment[]
  >([]);
  const [lastLoadedSpecText, setLastLoadedSpecText] = useState<string>(
    formatAgentSpec(defaultAgentSpec),
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [publishing, setPublishing] = useState<boolean>(false);
  const [publishingVersionId, setPublishingVersionId] = useState<string | null>(
    null,
  );
  const [newAgentName, setNewAgentName] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [testCallOpen, setTestCallOpen] = useState(false);
  const [jsonDrawerOpen, setJsonDrawerOpen] = useState(false);
  const loadedVersion = useMemo(() => {
    if (!currentAgentWithVersions) return null;
    return (
      [...currentAgentWithVersions.versions].sort(
        (a, b) => b.version_number - a.version_number,
      )[0] ?? null
    );
  }, [currentAgentWithVersions]);
  const hasUnsavedChanges = loadedVersion
    ? jsonText !== lastLoadedSpecText
    : true;
  const saveDisabledReason = getSaveDisabledReason({
    saving,
    selectedAgentId,
    detailReady,
    validation,
    jsonError,
  });
  const publishDisabledReason = getPublishDisabledReason({
    publishing,
    selectedAgentId,
    currentAgentWithVersions,
    hasUnsavedChanges,
  });

  const selectAgent = useCallback((agentId: string) => {
    selectedAgentIdRef.current = agentId;
    selectionGeneration.current += 1;
    detailRequestId.current += 1;
    setCurrentAgentWithVersions(null);
    setToolAttachments([]);
    setSelectedAgentId(agentId);
  }, []);

  const loadAgents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [loadedAgents, loadedFolders] = await Promise.all([
        fetchAgents(),
        fetchAgentFolders(),
      ]);
      setAgents(loadedAgents);
      setFolders(loadedFolders);
      if (loadedAgents.length > 0 && !selectedAgentId) {
        selectAgent(loadedAgents[0].id);
      }
    } catch (err) {
      if (isUnauthorizedApiError(err)) {
        onSessionExpired();
        return;
      }
      const message =
        err instanceof ConsoleApiError ? err.message : "Failed to load agents";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [onSessionExpired, selectedAgentId, selectAgent]);

  const loadAgentDetail = useCallback(
    async (agentId: string) => {
      // An old save/publish may finish after selection changed. It may complete
      // its own write, but must not start a reload that displaces the new editor.
      if (selectedAgentIdRef.current !== agentId) return;
      const requestId = ++detailRequestId.current;
      setCurrentAgentWithVersions(null);
      setToolAttachments([]);
      try {
        setLoading(true);
        setError(null);
        const agentWithVersions = await fetchAgentWithVersions(agentId);
        if (requestId !== detailRequestId.current) return;

        const latestVersion = [...agentWithVersions.versions].sort(
          (a, b) => b.version_number - a.version_number,
        )[0];
        if (latestVersion) {
          const shapeValidation = validateAgentSpec(latestVersion.spec_json, { runtimeSupport: false });
          if (!shapeValidation.valid) {
            setError(`Cannot open saved agent configuration: ${shapeValidation.messages.join(' ')}`);
            return;
          }
          const nextSpec = coerceValidAgentSpec(latestVersion.spec_json);
          const attachments = await fetchAgentVersionToolAttachments(latestVersion.id);
          if (requestId !== detailRequestId.current) return;
          replaceSpec(nextSpec, { markPersisted: true });
          setToolAttachments(attachments);
        } else {
          replaceSpec(createStarterSpecForAgent(agentWithVersions.agent));
          setToolAttachments([]);
        }
        setCurrentAgentWithVersions(agentWithVersions);
      } catch (err) {
        if (requestId !== detailRequestId.current) return;
        if (isUnauthorizedApiError(err)) {
          onSessionExpired();
          return;
        }
        const message =
          err instanceof ConsoleApiError
            ? err.message
            : "Failed to load agent detail";
        setError(message);
      } finally {
        if (requestId === detailRequestId.current) setLoading(false);
      }
    },
    [onSessionExpired],
  );

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void loadAgents();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [loadAgents]);

  useEffect(() => {
    if (!selectedAgentId) return;
    const handle = window.setTimeout(() => {
      void loadAgentDetail(selectedAgentId);
    }, 0);
    return () => {
      window.clearTimeout(handle);
      detailRequestId.current += 1;
    };
  }, [loadAgentDetail, selectedAgentId]);

  async function handleCreateNewAgent() {
    const name = newAgentName.trim();
    if (name.length === 0) {
      setError("Enter an agent name before creating an agent.");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const newAgent = await createAgent(name);
      setAgents([...agents, newAgent]);
      selectAgent(newAgent.id);
      setNewAgentName("");
      setSuccessMessage(`Agent "${name}" created successfully`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      if (isUnauthorizedApiError(err)) {
        onSessionExpired();
        return;
      }
      const message =
        err instanceof ConsoleApiError ? err.message : "Failed to create agent";
      setError(message);
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateFolder() {
    const name = newFolderName.trim();
    if (name.length === 0) {
      setError("Enter a folder name before creating a folder.");
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await createAgentFolder(name);
      setNewFolderName("");
      setSelectedFolderId("all");
      await loadAgents();
      setSuccessMessage(`Folder "${name}" created successfully`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      if (isUnauthorizedApiError(err)) {
        onSessionExpired();
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to create folder");
    } finally {
      setSaving(false);
    }
  }

  async function handleRenameFolder(folder: ApiAgentFolder) {
    const nextName = window.prompt("Rename folder", folder.name)?.trim();
    if (!nextName || nextName === folder.name) return;
    try {
      setSaving(true);
      setError(null);
      await updateAgentFolder(folder.id, { name: nextName });
      await loadAgents();
      setSuccessMessage(`Folder renamed to "${nextName}"`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      if (isUnauthorizedApiError(err)) {
        onSessionExpired();
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to rename folder");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteFolder(folder: ApiAgentFolder) {
    if (
      !window.confirm(
        `Delete folder "${folder.name}"? Agents stay in this tenant and become unfiled.`,
      )
    )
      return;
    try {
      setSaving(true);
      setError(null);
      await deleteAgentFolder(folder.id);
      if (selectedFolderId === folder.id) setSelectedFolderId("all");
      await loadAgents();
      setSuccessMessage(`Folder "${folder.name}" deleted`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      if (isUnauthorizedApiError(err)) {
        onSessionExpired();
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to delete folder");
    } finally {
      setSaving(false);
    }
  }

  async function handleAssignFolder(agent: ApiAgent, folderId: string) {
    try {
      setSaving(true);
      setError(null);
      const updated = await assignAgentFolder(
        agent.id,
        folderId === "unfiled" ? null : folderId,
      );
      setAgents((current) =>
        current.map((item) => (item.id === updated.id ? updated : item)),
      );
      if (currentAgentWithVersions?.agent.id === updated.id) {
        setCurrentAgentWithVersions({
          ...currentAgentWithVersions,
          agent: updated,
        });
      }
      await loadAgents();
      setSuccessMessage(
        folderId === "unfiled"
          ? `Removed "${agent.name}" from its folder`
          : `Moved "${agent.name}"`,
      );
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      if (isUnauthorizedApiError(err)) {
        onSessionExpired();
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to move agent");
    } finally {
      setSaving(false);
    }
  }

  async function saveDraftVersion(): Promise<ApiAgentVersion | null> {
    if (!selectedAgentId) {
      setError("No agent selected");
      return null;
    }

    if (!detailReady) {
      setError("Load the selected agent details before saving.");
      return null;
    }

    if (!validation.valid || jsonError) {
      setError("Cannot save invalid spec. Fix validation errors first.");
      return null;
    }

    const generation = selectionGeneration.current;
    try {
      setSaving(true);
      setError(null);
      const newVersion = await createAgentVersion(
        selectedAgentId,
        spec as unknown as Record<string, unknown>,
      );
      if (generation === selectionGeneration.current) {
        setSuccessMessage(`Draft version ${newVersion.version_number} saved`);
        setTimeout(() => setSuccessMessage(null), 3000);
        await loadAgentDetail(selectedAgentId);
      }
      return newVersion;
    } catch (err) {
      if (isUnauthorizedApiError(err)) {
        onSessionExpired();
        return null;
      }
      const message =
        err instanceof ConsoleApiError ? err.message : "Failed to save draft";
      if (generation === selectionGeneration.current) setError(message);
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveDraft() {
    await saveDraftVersion();
  }

  async function handleSaveAndPublish() {
    const generation = selectionGeneration.current;
    if (hasUnsavedChanges) {
      const newVersion = await saveDraftVersion();
      if (!newVersion) return;
      await publishSpecificVersion(
        newVersion.id,
        `Draft v${newVersion.version_number} saved and published`,
        generation,
      );
      return;
    }
    await handlePublish();
  }

  async function handlePublish() {
    if (!currentAgentWithVersions) {
      setError("No agent selected");
      return;
    }

    if (hasUnsavedChanges) {
      setError(
        "Save your edits as a draft version before publishing. Publish only works on saved versions.",
      );
      return;
    }

    const latestVersion = [...currentAgentWithVersions.versions].sort(
      (a, b) => b.version_number - a.version_number,
    )[0];
    if (!latestVersion) {
      setError("No version to publish");
      return;
    }

    if (latestVersion.id === currentAgentWithVersions.agent.active_version_id) {
      setError("Latest version is already the active published version");
      return;
    }

    await publishSpecificVersion(
      latestVersion.id,
      `Version ${latestVersion.version_number} published successfully`,
    );
  }

  async function publishSpecificVersion(versionId: string, success: string, generation = selectionGeneration.current) {
    if (!selectedAgentId || !detailReady) {
      setError("Load the selected agent details before publishing.");
      return;
    }

    try {
      setPublishing(true);
      setPublishingVersionId(versionId);
      if (generation === selectionGeneration.current) setError(null);
      await publishAgentVersion(selectedAgentId, versionId);
      if (generation === selectionGeneration.current) {
        setSuccessMessage(success);
        setTimeout(() => setSuccessMessage(null), 3000);
        await loadAgentDetail(selectedAgentId);
        if (generation === selectionGeneration.current) await loadAgents();
      }
    } catch (err) {
      if (isUnauthorizedApiError(err)) {
        onSessionExpired();
        return;
      }
      const message =
        err instanceof ConsoleApiError
          ? err.message
          : "Failed to publish version";
      if (generation === selectionGeneration.current) setError(message);
    } finally {
      setPublishing(false);
      setPublishingVersionId(null);
    }
  }

  function replaceSpec(
    next: CallableAgentSpecV1,
    options: { markPersisted?: boolean } = {},
  ) {
    const formatted = formatAgentSpec(next);
    setSpec(next);
    setJsonText(formatted);
    setJsonError(null);
    setValidation(validateAgentSpec(next));
    if (options.markPersisted) setLastLoadedSpecText(formatted);
  }

  function updateSpec(
    mutator: (current: CallableAgentSpecV1) => CallableAgentSpecV1,
  ) {
    setSpec((current) => {
      const next = mutator(current);
      const formatted = formatAgentSpec(next);
      setJsonText(formatted);
      setJsonError(null);
      setValidation(validateAgentSpec(next));
      return next;
    });
  }

  function updateJson(nextText: string) {
    setJsonText(nextText);
    try {
      const parsed = parseAgentSpec(nextText);
      const nextSpec = coerceValidAgentSpec(parsed);
      setSpec(nextSpec);
      setJsonError(null);
      setValidation(validateAgentSpec(nextSpec));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Invalid Agent Spec JSON";
      setJsonError(message);
      setValidation({ valid: false, messages: [message] });
    }
  }

  function validateCurrentSpec() {
    if (jsonError) {
      setValidation({
        valid: false,
        messages: [`Invalid Agent Spec JSON: ${jsonError}`],
      });
      return;
    }
    setValidation(validateAgentSpec(spec));
  }

  const activeFolders = folders.filter((folder) => folder.status === "active");
  const filteredAgents = agents.filter((agent) => {
    if (selectedFolderId === "all") return true;
    if (selectedFolderId === "unfiled") return agent.folder_id === null;
    return agent.folder_id === selectedFolderId;
  });
  const unfiledCount = agents.filter(
    (agent) => agent.folder_id === null,
  ).length;

  // Unsaved edits come first: they are usually the reason publish is blocked,
  // and they are the thing the operator can act on.
  const builderStatus = selectedAgentId && !detailReady
    ? { tone: "warn", message: "Agent details are unavailable. Editing and saving are disabled until they load." }
    : hasUnsavedChanges
    ? {
        tone: "warn",
        message:
          "Unsaved edits. Save & publish, or Save draft to review first.",
      }
    : saveDisabledReason
      ? {
          tone: "warn",
          message: `Save draft unavailable: ${saveDisabledReason}`,
        }
      : publishDisabledReason
        ? {
            tone: "warn",
            message: `Publish unavailable: ${publishDisabledReason}`,
          }
        : currentAgentWithVersions?.agent.active_version_id
          ? { tone: "good", message: "Published and live on assigned DIDs." }
          : {
              tone: "",
              message:
                "Publish before assigning this agent to production traffic.",
            };

  return (
    <section
      className="screen agent-builder-screen"
      aria-labelledby="agents-heading"
    >
      <header className="screen-header">
        <div>
          <h1 id="agents-heading">Agents</h1>
          <p>
            Spec-first builder: every visual control maps to Agent Spec v1 JSON.
          </p>
          {error && (
            <div className="notice danger compact-notice">
              <strong>Error</strong>
              <p>{error}</p>
            </div>
          )}
          {successMessage && (
            <div className="notice success compact-notice">
              <strong>Success</strong>
              <p>{successMessage}</p>
            </div>
          )}
        </div>
        <div className="button-row">
          <button
            className="secondary-button"
            type="button"
            onClick={() => setJsonDrawerOpen(true)}
            disabled={!editorAvailable}
            aria-haspopup="dialog"
          >
            <Settings size={16} aria-hidden="true" />
            View config
          </button>
          <button
            className="secondary-button"
            type="button"
            onClick={() => setTestCallOpen(true)}
            aria-haspopup="dialog"
          >
            <PhoneCall size={16} aria-hidden="true" />
            Test call
          </button>
          <button
            className="secondary-button"
            type="button"
            onClick={handleSaveDraft}
            id="save-draft-button"
            disabled={Boolean(saveDisabledReason) || publishing}
            title={saveDisabledReason ?? undefined}
          >
            {saving ? (
              <Loader2 size={16} className="spinning" aria-hidden="true" />
            ) : (
              <Save size={16} aria-hidden="true" />
            )}
            {saving ? "Saving..." : "Save draft"}
          </button>
          <button
            className="secondary-button"
            type="button"
            onClick={handlePublish}
            disabled={Boolean(publishDisabledReason) || saving}
            title={publishDisabledReason ?? undefined}
          >
            {publishing ? (
              <Loader2 size={16} className="spinning" aria-hidden="true" />
            ) : (
              <Plus size={16} aria-hidden="true" />
            )}
            {publishing ? "Publishing..." : "Publish saved"}
          </button>
          <button
            className="primary-button"
            type="button"
            onClick={handleSaveAndPublish}
            disabled={Boolean(saveDisabledReason) || saving || publishing}
            title={
              saveDisabledReason ??
              "Save a draft version, then publish that exact version."
            }
          >
            {saving || publishing ? (
              <Loader2 size={16} className="spinning" aria-hidden="true" />
            ) : (
              <Plus size={16} aria-hidden="true" />
            )}
            {saving || publishing ? "Working..." : "Save & publish"}
          </button>
        </div>
      </header>

      {/* One status line, not four. Blockers outrank unsaved edits, which outrank
          the in-sync case, so the operator sees the thing that stops them first. */}
      <div className="builder-action-state" aria-live="polite">
        <div className="builder-version-context">
          <strong>
            {loadedVersion
              ? `Editing v${loadedVersion.version_number}`
              : "No saved version loaded"}
          </strong>
          <span className={builderStatus.tone}>{builderStatus.message}</span>
        </div>
      </div>

      <div className="agent-layout">
        <aside className="panel agent-list-panel" aria-label="Agents">
          <div className="panel-title-row">
            <h2>Agent folders</h2>
            <span className="table-label">Tenant scoped</span>
          </div>
          <form
            className="new-agent-form"
            onSubmit={(event) => {
              event.preventDefault();
              void handleCreateFolder();
            }}
          >
            <label className="sr-only" htmlFor="new-folder-name">
              New folder name
            </label>
            <input
              id="new-folder-name"
              placeholder="New folder"
              value={newFolderName}
              onChange={(event) => setNewFolderName(event.target.value)}
            />
            <button
              className="secondary-button"
              type="submit"
              disabled={saving || newFolderName.trim().length === 0}
            >
              <Plus size={14} aria-hidden="true" /> Folder
            </button>
          </form>
          <div className="folder-filter-list" aria-label="Agent folder filters">
            <button
              className={`folder-filter ${selectedFolderId === "all" ? "active" : ""}`}
              type="button"
              onClick={() => setSelectedFolderId("all")}
            >
              <span>All agents</span>
              <small>{agents.length}</small>
            </button>
            <button
              className={`folder-filter ${selectedFolderId === "unfiled" ? "active" : ""}`}
              type="button"
              onClick={() => setSelectedFolderId("unfiled")}
            >
              <span>Unfiled</span>
              <small>{unfiledCount}</small>
            </button>
            {activeFolders.map((folder) => (
              <div
                className={`folder-filter-row ${selectedFolderId === folder.id ? "active" : ""}`}
                key={folder.id}
              >
                <button
                  className="folder-filter"
                  type="button"
                  onClick={() => setSelectedFolderId(folder.id)}
                >
                  <span>{folder.name}</span>
                  <small>{folder.agent_count}</small>
                </button>
                <div className="folder-row-actions">
                  <button
                    className="icon-action"
                    type="button"
                    aria-label={`Rename ${folder.name}`}
                    onClick={() => void handleRenameFolder(folder)}
                  >
                    Rename
                  </button>
                  <button
                    className="icon-action"
                    type="button"
                    aria-label={`Delete ${folder.name}`}
                    onClick={() => void handleDeleteFolder(folder)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="panel-title-row agent-list-heading">
            <h2>Agent list</h2>
            <span className="table-label">{filteredAgents.length} shown</span>
          </div>
          <form
            className="new-agent-form"
            onSubmit={(event) => {
              event.preventDefault();
              void handleCreateNewAgent();
            }}
          >
            <label className="sr-only" htmlFor="new-agent-name">
              New agent name
            </label>
            <input
              id="new-agent-name"
              placeholder="New agent name"
              value={newAgentName}
              onChange={(event) => setNewAgentName(event.target.value)}
            />
            <button
              className="secondary-button"
              type="submit"
              disabled={saving || newAgentName.trim().length === 0}
            >
              <Plus size={14} aria-hidden="true" /> New
            </button>
          </form>
          {loading && agents.length === 0 && (
            <p className="compact-copy">Loading agents...</p>
          )}
          {agents.length === 0 && !loading && (
            <p className="compact-copy">
              No agents yet. Create one to get started.
            </p>
          )}
          {agents.length > 0 && filteredAgents.length === 0 && (
            <p className="compact-copy">No agents in this folder yet.</p>
          )}
          {filteredAgents.map((agent) => (
            <article
              className={`agent-list-item ${selectedAgentId === agent.id ? "active" : ""}`}
              key={agent.id}
            >
              <button
                className="agent-open-button"
                type="button"
                onClick={() => {
                  if (agent.id === selectedAgentId) {
                    void loadAgentDetail(agent.id);
                    return;
                  }
                  selectAgent(agent.id);
                }}
              >
                <strong>{agent.name}</strong>
                <span>
                  {agent.status} · {agent.folder_name ?? "Unfiled"}
                </span>
                <small>
                  {agent.active_version_id
                    ? "Published version exists"
                    : "No published version"}
                </small>
              </button>
              <label className="folder-assignment">
                <span>Folder</span>
                <select
                  value={agent.folder_id ?? "unfiled"}
                  onChange={(event) =>
                    void handleAssignFolder(agent, event.target.value)
                  }
                  disabled={saving}
                >
                  <option value="unfiled">Unfiled</option>
                  {activeFolders.map((folder) => (
                    <option value={folder.id} key={folder.id}>
                      {folder.name}
                    </option>
                  ))}
                </select>
              </label>
            </article>
          ))}
        </aside>

        <div className="agent-editor-grid">
          {loading && <p className="compact-copy">Syncing live agents...</p>}
          {!selectedAgentId && (
            <p className="compact-copy">
              Editing the local draft spec until a live agent is selected.
            </p>
          )}
          {!editorAvailable && (
            <p className="compact-copy">Select the agent again to retry loading its saved configuration.</p>
          )}
          {editorAvailable && <>
          <VisualSpecEditor
            spec={spec}
            onChange={updateSpec}
            disabled={Boolean(jsonError) || saving || publishing}
            invalidJson={Boolean(jsonError)}
          />
          <McpPermissionsPanel
            loadedVersion={loadedVersion}
            attachments={toolAttachments}
            onSessionExpired={onSessionExpired}
            onUpdated={(updated) =>
              setToolAttachments((current) =>
                current.map((item) =>
                  item.attachment_id === updated.attachment_id ? updated : item,
                ),
              )
            }
          />
          <SpecContractShell
            currentAgent={currentAgentWithVersions}
            editingSpec={spec}
            onLoadVersion={(version) =>
              replaceSpec(coerceValidAgentSpec(version.spec_json), {
                markPersisted: true,
              })
            }
            onPublishVersion={(version) =>
              publishSpecificVersion(
                version.id,
                version.status === "draft"
                  ? `Version ${version.version_number} published successfully`
                  : `Rolled back to version ${version.version_number}`,
              )
            }
            publishingVersionId={publishingVersionId}
            operationPending={saving || publishing}
          />
          </>}
        </div>
      </div>

      <SideDrawer
        title="Test call"
        open={testCallOpen}
        onClose={() => setTestCallOpen(false)}
        description="Place an outbound UAT call with this agent's published version."
      >
        <TestCallPanel
          agentId={selectedAgentId}
          agentName={currentAgentWithVersions?.agent.name ?? null}
          hasPublishedVersion={Boolean(
            currentAgentWithVersions?.agent.active_version_id,
          )}
          onSessionExpired={onSessionExpired}
        />
      </SideDrawer>
      <SideDrawer
        title="Agent Spec config"
        open={jsonDrawerOpen}
        onClose={() => setJsonDrawerOpen(false)}
        description="View and edit the raw Agent Spec JSON for this visual editor state."
      >
        {editorAvailable && <JsonSpecEditor
          jsonText={jsonText}
          jsonError={jsonError}
          validation={validation}
          onJsonChange={updateJson}
          onValidate={validateCurrentSpec}
          disabled={saving || publishing}
        />}
      </SideDrawer>
    </section>
  );
}

function SideDrawer(props: {
  title: string;
  description: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (props.open && !dialog.open) dialog.showModal();
    if (!props.open && dialog.open) dialog.close();
  }, [props.open]);

  return (
    <dialog
      className="side-drawer-dialog"
      ref={ref}
      onCancel={props.onClose}
      onClose={props.onClose}
    >
      <div className="side-drawer-body">
        <header className="side-drawer-header">
          <div>
            <h2>{props.title}</h2>
            <p>{props.description}</p>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={props.onClose}
            aria-label={`Close ${props.title}`}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>
        {props.children}
      </div>
    </dialog>
  );
}

function createStarterSpecForAgent(agent: ApiAgent): CallableAgentSpecV1 {
  return {
    ...defaultAgentSpec,
    name: agent.name,
    description: null,
    conversation: {
      ...defaultAgentSpec.conversation,
      first_message: "Hi, how can I help?",
      system_prompt: `You are ${agent.name}, a concise and helpful phone agent.`,
    },
  };
}

function McpPermissionsPanel(props: {
  loadedVersion: ApiAgentVersion | null;
  attachments: ApiAgentVersionToolAttachment[];
  onUpdated: (attachment: ApiAgentVersionToolAttachment) => void;
  onSessionExpired: () => void;
}) {
  const [savingAttachmentId, setSavingAttachmentId] = useState<string | null>(
    null,
  );
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mcpAttachments = props.attachments.filter((attachment) =>
    hasMcpReviewShape(attachment),
  );

  async function saveAttachment(
    attachment: ApiAgentVersionToolAttachment,
    tools: ApiMcpPermissionTool[],
  ) {
    if (!props.loadedVersion) return;
    const mutatingCount = tools.filter(
      (tool) => tool.classification === "mutating",
    ).length;
    if (
      mutatingCount > 0 &&
      !window.confirm(
        `Approve ${mutatingCount} mutating MCP tool(s)? These can change external systems and will be audited.`,
      )
    )
      return;
    try {
      setSavingAttachmentId(attachment.attachment_id);
      setError(null);
      const updated = await updateAgentVersionMcpPermissions(
        props.loadedVersion.id,
        attachment.attachment_id,
        {
          enabled: tools.length > 0,
          tools,
        },
      );
      props.onUpdated(updated);
      setMessage(`Saved MCP permissions for ${tools.length} tool(s).`);
    } catch (err) {
      if (isUnauthorizedApiError(err)) {
        props.onSessionExpired();
        return;
      }
      setError(
        err instanceof ConsoleApiError
          ? err.message
          : "Failed to save MCP permissions",
      );
    } finally {
      setSavingAttachmentId(null);
    }
  }

  return (
    <section
      className="panel builder-panel"
      aria-labelledby="mcp-permissions-heading"
    >
      <div className="panel-title-row">
        <h2 id="mcp-permissions-heading">MCP review and approval</h2>
        <span className="table-label">Runtime allowlist</span>
      </div>
      <p className="compact-copy">
        Approve exact MCP tool names for the loaded saved version. Mutating
        tools require explicit confirmation. Secrets, tool arguments, raw
        results, prompts, transcripts, and provider payloads are never shown
        here.
      </p>
      {message ? (
        <div className="notice success compact-notice">
          <strong>Saved</strong>
          <p>{message}</p>
        </div>
      ) : null}
      {error ? (
        <div className="notice danger compact-notice">
          <strong>Error</strong>
          <p>{error}</p>
        </div>
      ) : null}
      {!props.loadedVersion ? (
        <div className="empty-action">
          <p className="compact-copy">
            <strong>Save a version to unlock approvals.</strong> MCP permissions
            belong to an immutable saved version, so there is nothing to approve
            in this draft.
          </p>
          <button
            className="secondary-button compact-action"
            type="button"
            onClick={() =>
              document.getElementById("save-draft-button")?.focus()
            }
          >
            Save version first
          </button>
        </div>
      ) : null}
      {props.loadedVersion && mcpAttachments.length === 0 ? (
        <div className="empty-action">
          <p className="compact-copy">
            <strong>No MCP attachments yet.</strong> Set up or attach an MCP
            tool, then return here to approve its exact catalog.
          </p>
          <a className="secondary-button compact-action" href="#tools-mcp">
            Set up an MCP tool
          </a>
        </div>
      ) : null}
      {mcpAttachments.map((attachment) => {
        const catalog = catalogToolsFromAttachment(attachment);
        const approved = permissionToolsFromAttachment(attachment);
        return (
          <McpAttachmentCard
            attachment={attachment}
            catalog={catalog}
            approved={approved}
            key={attachment.attachment_id}
            saving={savingAttachmentId === attachment.attachment_id}
            onSave={(tools) => void saveAttachment(attachment, tools)}
          />
        );
      })}
    </section>
  );
}

function McpAttachmentCard(props: {
  attachment: ApiAgentVersionToolAttachment;
  catalog: ApiMcpPermissionTool[];
  approved: ApiMcpPermissionTool[];
  saving: boolean;
  onSave: (tools: ApiMcpPermissionTool[]) => void;
}) {
  const [selected, setSelected] = useState<
    Record<string, ApiMcpPermissionTool>
  >(() => Object.fromEntries(props.approved.map((tool) => [tool.name, tool])));
  const tools = props.catalog.length > 0 ? props.catalog : props.approved;
  const selectedTools = Object.values(selected).sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  function toggleTool(tool: ApiMcpPermissionTool, checked: boolean) {
    setSelected((current) => {
      const next = { ...current };
      if (checked) next[tool.name] = tool;
      else delete next[tool.name];
      return next;
    });
  }

  function classifyTool(
    tool: ApiMcpPermissionTool,
    classification: ApiMcpPermissionTool["classification"],
  ) {
    setSelected((current) => ({
      ...current,
      [tool.name]: {
        ...tool,
        classification,
        allow_mutating: classification === "mutating",
      },
    }));
  }

  return (
    <div className="table-card compact-card">
      <div className="panel-title-row">
        <h3>Attachment {props.attachment.display_order}</h3>
        <span className="mono table-label">
          {props.attachment.attachment_id}
        </span>
      </div>
      {tools.length === 0 ? (
        <p className="compact-copy">
          No discovered catalog snapshot is stored for this attachment.
        </p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Allow</th>
              <th>Tool name</th>
              <th>Classification</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {tools.map((tool) => {
              const current = selected[tool.name];
              return (
                <tr key={tool.name}>
                  <td>
                    <input
                      type="checkbox"
                      checked={Boolean(current)}
                      onChange={(event) =>
                        toggleTool(tool, event.target.checked)
                      }
                      aria-label={`Allow MCP tool ${tool.name}`}
                    />
                  </td>
                  <td className="mono strong-cell">{tool.name}</td>
                  <td>
                    <select
                      className="compact-select"
                      value={current?.classification ?? tool.classification}
                      disabled={!current}
                      onChange={(event) =>
                        classifyTool(
                          tool,
                          event.target
                            .value as ApiMcpPermissionTool["classification"],
                        )
                      }
                    >
                      <option value="read_only">Read only</option>
                      <option value="mutating">Mutating</option>
                    </select>
                  </td>
                  <td>{tool.description ?? "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <div className="panel-actions">
        <button
          className="secondary-button compact-action"
          type="button"
          disabled={props.saving}
          onClick={() => props.onSave(selectedTools)}
        >
          {props.saving
            ? "Saving…"
            : `Save ${selectedTools.length} approval(s)`}
        </button>
      </div>
    </div>
  );
}

function hasMcpReviewShape(attachment: ApiAgentVersionToolAttachment): boolean {
  const config = attachment.attachment_config;
  return Boolean(config.mcp_permissions || config.mcp_discovered_catalog);
}

function permissionToolsFromAttachment(
  attachment: ApiAgentVersionToolAttachment,
): ApiMcpPermissionTool[] {
  const permissions = attachment.attachment_config.mcp_permissions;
  if (!isRecord(permissions) || !Array.isArray(permissions.tools)) return [];
  return permissions.tools
    .map(coerceMcpPermissionTool)
    .filter((tool): tool is ApiMcpPermissionTool => Boolean(tool));
}

function catalogToolsFromAttachment(
  attachment: ApiAgentVersionToolAttachment,
): ApiMcpPermissionTool[] {
  const catalog = attachment.attachment_config.mcp_discovered_catalog;
  const rawTools = Array.isArray(catalog)
    ? catalog
    : isRecord(catalog) && Array.isArray(catalog.tools)
      ? catalog.tools
      : [];
  return rawTools
    .map(coerceMcpPermissionTool)
    .filter((tool): tool is ApiMcpPermissionTool => Boolean(tool));
}

function coerceMcpPermissionTool(value: unknown): ApiMcpPermissionTool | null {
  if (
    !isRecord(value) ||
    typeof value.name !== "string" ||
    value.name.trim().length === 0
  )
    return null;
  const classification =
    value.classification === "mutating" ? "mutating" : "read_only";
  return {
    name: value.name,
    classification,
    allow_mutating: classification === "mutating" ? true : undefined,
    description:
      typeof value.description === "string" ? value.description : null,
    input_schema: isRecord(value.input_schema) ? value.input_schema : undefined,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const contactMemoryPromptBlock = `\n\n# CONTACT MEMORY BEHAVIOUR\nContact memory is relationship context, not a script. Use it to make the conversation accurate and natural.\n- Do not quote memory summaries verbatim.\n- Do not say this is the first time speaking if memory indicates prior contact.\n- If any prompt wording conflicts with contact memory, prefer the memory and speak naturally.\n- Acknowledge prior context briefly only when it helps the conversation.\n- Never mention internal notes, databases, metadata, or memory systems to the caller.\n- Memory summary: {{ contact.memory_summary }}\n- Last interaction: {{ contact.last_interaction_summary }}\n- Agent next step: {{ contact.agent_next_step }}`;

const contactMemoryInputs = [
  {
    name: "contact.memory_summary",
    source: "contact",
    type: "string",
    required: false,
    missing_value_policy: "omit",
  },
  {
    name: "contact.last_interaction_summary",
    source: "contact",
    type: "string",
    required: false,
    missing_value_policy: "omit",
  },
  {
    name: "contact.last_interaction_at",
    source: "contact",
    type: "datetime",
    required: false,
    missing_value_policy: "omit",
  },
  {
    name: "contact.agent_memory_summary",
    source: "contact",
    type: "string",
    required: false,
    missing_value_policy: "omit",
  },
  {
    name: "contact.agent_next_step",
    source: "contact",
    type: "string",
    required: false,
    missing_value_policy: "omit",
  },
];

const contactMemorySampleContext: Record<string, string> = {
  "contact.memory_summary":
    "James asked about pricing and prefers an afternoon callback.",
  "contact.last_interaction_summary":
    "Sarah spoke with James yesterday about plan options.",
  "contact.last_interaction_at": "2026-09-25T15:00:00+10:00",
  "contact.agent_memory_summary":
    "This agent previously discussed pricing with James.",
  "contact.agent_next_step":
    "Offer to continue the pricing conversation and confirm a callback window.",
};

function getContextInputs(
  spec: CallableAgentSpecV1,
): Record<string, unknown>[] {
  if (!isRecord(spec.context) || !Array.isArray(spec.context.inputs)) return [];
  return spec.context.inputs.filter(isRecord).map((input) => ({ ...input }));
}

function hasContactMemoryContext(spec: CallableAgentSpecV1): boolean {
  const names = new Set(getContextInputs(spec).map((input) => input.name));
  return contactMemoryInputs.every((input) => names.has(input.name));
}

function withContactMemoryContext(
  spec: CallableAgentSpecV1,
): CallableAgentSpecV1 {
  const context = isRecord(spec.context) ? { ...spec.context } : {};
  const existingInputs = getContextInputs(spec);
  const existingNames = new Set(existingInputs.map((input) => input.name));
  const nextInputs = [
    ...existingInputs,
    ...contactMemoryInputs
      .filter((input) => !existingNames.has(input.name))
      .map((input) => ({ ...input })),
  ];
  const sampleContext = isRecord(context.sample_context)
    ? { ...context.sample_context }
    : {};
  for (const [key, value] of Object.entries(contactMemorySampleContext)) {
    if (sampleContext[key] === undefined) sampleContext[key] = value;
  }
  const systemPrompt = spec.conversation.system_prompt.includes(
    "# CONTACT MEMORY BEHAVIOUR",
  )
    ? spec.conversation.system_prompt
    : `${spec.conversation.system_prompt.trimEnd()}${contactMemoryPromptBlock}`;
  return {
    ...spec,
    conversation: { ...spec.conversation, system_prompt: systemPrompt },
    context: { ...context, inputs: nextInputs, sample_context: sampleContext },
  };
}

function MemoryContextHelper(props: {
  spec: CallableAgentSpecV1;
  disabled: boolean;
  onChange: (
    mutator: (current: CallableAgentSpecV1) => CallableAgentSpecV1,
  ) => void;
}) {
  const enabled = hasContactMemoryContext(props.spec);
  return (
    <div className="notice compact-notice">
      <div className="panel-title-row">
        <strong>
          <Sparkles size={14} aria-hidden="true" /> Contact memory
        </strong>
        <span className="table-label">{enabled ? "Declared" : "Optional"}</span>
      </div>
      <p>
        Add safe contact-memory variables to this Agent Spec so outbound calls
        can receive prior interaction context when a contact_id is present.
      </p>
      <button
        className="secondary-button compact-action"
        type="button"
        disabled={props.disabled || enabled}
        onClick={() => props.onChange(withContactMemoryContext)}
      >
        Declare memory variables
      </button>
    </div>
  );
}

function getSaveDisabledReason(props: {
  saving: boolean;
  selectedAgentId: string | null;
  detailReady: boolean;
  validation: ValidationResult;
  jsonError: string | null;
}): string | null {
  if (props.saving) return "Save is already in progress.";
  if (!props.selectedAgentId) return "Select or create an agent first.";
  if (!props.detailReady) return "Load the selected agent details before saving.";
  if (props.jsonError) return "Fix invalid JSON first.";
  if (!props.validation.valid) return "Fix Agent Spec validation errors first.";
  return null;
}

function getPublishDisabledReason(props: {
  publishing: boolean;
  selectedAgentId: string | null;
  currentAgentWithVersions: ApiAgentWithVersions | null;
  hasUnsavedChanges: boolean;
}): string | null {
  if (props.publishing) return "Publish is already in progress.";
  if (!props.selectedAgentId || !props.currentAgentWithVersions)
    return "Select an agent with a saved version first.";
  if (props.hasUnsavedChanges)
    return "Save draft before publishing; publish only works on saved versions.";
  return null;
}

function TurnTakingOverrideNotice(props: {
  spec: CallableAgentSpecV1;
  disabled: boolean;
  onChange: (
    mutator: (current: CallableAgentSpecV1) => CallableAgentSpecV1,
  ) => void;
}) {
  const overrides = turnTakingOverrides(props.spec);
  if (overrides.length === 0) return null;
  return (
    <div className="notice warning compact-notice" role="status">
      <strong>Imported values override these fields</strong>
      <p>
        These values in <code>providers.stt.settings</code> override the matching fields below: {overrides.map((item) => `${item.field} ${item.display}`).join(", ")}. A Vapi-compatible import or update stores them there, and the next one can set them again.
      </p>
      <button className="secondary-button compact-action" type="button" disabled={props.disabled} onClick={() => props.onChange(clearTurnTakingOverrides)}>
        Remove imported values
      </button>
    </div>
  );
}

function VisualSpecEditor(props: {
  spec: CallableAgentSpecV1;
  disabled: boolean;
  invalidJson: boolean;
  onChange: (
    mutator: (current: CallableAgentSpecV1) => CallableAgentSpecV1,
  ) => void;
}) {
  const { spec } = props;
  return (
    <section
      className="panel builder-panel"
      aria-labelledby="visual-editor-heading"
    >
      <div className="panel-title-row">
        <h2 id="visual-editor-heading">Visual editor</h2>
        <span className="table-label">Agent Spec v1 fields</span>
      </div>
      {props.invalidJson ? (
        <div className="notice danger compact-notice">
          <strong>Fix JSON first</strong>
          <p>
            Visual controls keep showing the last valid object so invalid JSON
            cannot create hidden state.
          </p>
        </div>
      ) : null}

      <SectionTitle>Identity</SectionTitle>
      <div className="form-grid two-columns">
        <TextField
          label="Name"
          value={spec.name}
          disabled={props.disabled}
          onChange={(value) =>
            props.onChange((current) => ({ ...current, name: value }))
          }
        />
        <TextField
          label="Description"
          value={spec.description ?? ""}
          disabled={props.disabled}
          onChange={(value) =>
            props.onChange((current) => ({
              ...current,
              description: value || null,
            }))
          }
        />
      </div>

      <SectionTitle>Conversation</SectionTitle>
      <div className="form-grid two-columns">
        <SelectField
          label="First message mode"
          value={spec.conversation.first_message_mode ?? "speak"}
          disabled={props.disabled}
          options={["speak", "wait_for_caller"]}
          onChange={(value) =>
            props.onChange((current) => ({
              ...current,
              conversation: {
                ...current.conversation,
                first_message_mode: value as FirstMessageMode,
              },
            }))
          }
        />
        <TextField
          label="First message"
          value={spec.conversation.first_message}
          disabled={props.disabled}
          onChange={(value) =>
            props.onChange((current) => ({
              ...current,
              conversation: { ...current.conversation, first_message: value },
            }))
          }
        />
      </div>
      <TextAreaField
        label="System prompt"
        value={spec.conversation.system_prompt}
        disabled={props.disabled}
        onChange={(value) =>
          props.onChange((current) => ({
            ...current,
            conversation: { ...current.conversation, system_prompt: value },
          }))
        }
      />
      <MemoryContextHelper
        spec={spec}
        disabled={props.disabled}
        onChange={props.onChange}
      />

      <SectionTitle>Endpointing and turn-taking</SectionTitle>
      <TurnTakingOverrideNotice spec={spec} disabled={props.disabled} onChange={props.onChange} />
      <div className="form-grid three-columns">
        <SelectField
          label="Endpointing mode"
          value={spec.conversation.endpointing.mode}
          disabled={props.disabled}
          options={["hybrid", "server_vad", "stt_final"]}
          onChange={(value) =>
            props.onChange((current) => ({
              ...current,
              conversation: {
                ...current.conversation,
                endpointing: {
                  ...current.conversation.endpointing,
                  mode: value as EndpointingMode,
                },
              },
            }))
          }
        />
        <NumberField
          label="Min silence ms"
          value={spec.conversation.endpointing.min_silence_ms}
          disabled={props.disabled}
          onChange={(value) =>
            props.onChange((current) => ({
              ...current,
              conversation: {
                ...current.conversation,
                endpointing: {
                  ...current.conversation.endpointing,
                  min_silence_ms: value,
                },
              },
            }))
          }
        />
        <NumberField
          label="Speech final timeout ms"
          value={spec.conversation.endpointing.speech_final_timeout_ms}
          disabled={props.disabled}
          onChange={(value) =>
            props.onChange((current) => ({
              ...current,
              conversation: {
                ...current.conversation,
                endpointing: {
                  ...current.conversation.endpointing,
                  speech_final_timeout_ms: value,
                },
              },
            }))
          }
        />
        <NumberField
          label="Silence timeout seconds"
          value={spec.conversation.silence_timeout_seconds}
          disabled={props.disabled}
          onChange={(value) =>
            props.onChange((current) => ({
              ...current,
              conversation: {
                ...current.conversation,
                silence_timeout_seconds: value,
              },
            }))
          }
        />
        <NumberField
          label="Max call duration seconds"
          value={spec.conversation.max_call_duration_seconds}
          disabled={props.disabled}
          onChange={(value) =>
            props.onChange((current) => ({
              ...current,
              conversation: {
                ...current.conversation,
                max_call_duration_seconds: value,
              },
            }))
          }
        />
        <NumberField
          label="Interruption min words"
          value={spec.conversation.interruption_min_words ?? 3}
          disabled={props.disabled}
          onChange={(value) =>
            props.onChange((current) => ({
              ...current,
              conversation: {
                ...current.conversation,
                interruption_min_words: value,
              },
            }))
          }
        />
        <label className="checkbox-field">
          <input
            type="checkbox"
            disabled={props.disabled}
            checked={spec.conversation.allow_interruptions}
            onChange={(event) =>
              props.onChange((current) => ({
                ...current,
                conversation: {
                  ...current.conversation,
                  allow_interruptions: event.target.checked,
                },
              }))
            }
          />{" "}
          Allow interruptions
        </label>
        <CheckboxField
          label="Preemptive TTS for Flux (faster, may spend discarded TTS)"
          checked={preemptiveTtsEnabled(spec)}
          disabled={props.disabled}
          onChange={(checked) =>
            props.onChange((current) => setPreemptiveTtsEnabled(current, checked))
          }
        />
      </div>

      <SectionTitle>Background ambience</SectionTitle>
      <div className="form-grid three-columns">
        <CheckboxField label="Enable background ambience" checked={Boolean(spec.conversation.ambience?.enabled)} disabled={props.disabled} onChange={(checked) => props.onChange((current) => ({ ...current, conversation: { ...current.conversation, ambience: { enabled: checked, preset: current.conversation.ambience?.preset ?? 'office', volume_db: current.conversation.ambience?.volume_db ?? -18 } } }))} />
        <SelectField label="Ambience preset" value={spec.conversation.ambience?.preset ?? 'office'} disabled={props.disabled || !spec.conversation.ambience?.enabled} options={['office', 'restaurant', 'lounge']} onChange={(value) => props.onChange((current) => ({ ...current, conversation: { ...current.conversation, ambience: { enabled: current.conversation.ambience?.enabled ?? true, preset: value as AmbiencePreset, volume_db: current.conversation.ambience?.volume_db ?? -18 } } }))} />
        <AmbienceVolumeField value={spec.conversation.ambience?.volume_db ?? -18} disabled={props.disabled || !spec.conversation.ambience?.enabled} onChange={(value) => props.onChange((current) => ({ ...current, conversation: { ...current.conversation, ambience: { enabled: current.conversation.ambience?.enabled ?? true, preset: current.conversation.ambience?.preset ?? 'office', volume_db: value } } }))} />
      </div>
      <p className="compact-copy muted-row">Ambience is mixed into the caller's room. Start around -18 dB for PSTN calls; use -12 dB for an obvious UAT check, and -6 dB only when you deliberately want it prominent.</p>

      <SectionTitle>Providers</SectionTitle>
      <div className="form-grid three-columns">
        <SelectField
          label="STT provider"
          value={spec.providers.stt.provider}
          disabled={props.disabled}
          options={["deepgram"]}
          onChange={(value) =>
            props.onChange((current) => ({
              ...current,
              providers: {
                ...current.providers,
                stt: {
                  ...current.providers.stt,
                  provider: value as SttProvider,
                },
              },
            }))
          }
        />
        <SelectField
          label="STT model"
          value={spec.providers.stt.model}
          disabled={props.disabled}
          options={["flux-general-en", "flux-general-multi", "nova-3"]}
          onChange={(value) =>
            props.onChange((current) => ({
              ...current,
              providers: {
                ...current.providers,
                stt: { ...current.providers.stt, model: value },
              },
            }))
          }
        />
        <TextField
          label="STT language"
          value={spec.providers.stt.language}
          disabled={props.disabled}
          onChange={(value) =>
            props.onChange((current) => ({
              ...current,
              providers: {
                ...current.providers,
                stt: { ...current.providers.stt, language: value },
              },
            }))
          }
        />
        <SelectField
          label="LLM provider"
          value={spec.providers.llm.provider}
          disabled={props.disabled}
          options={["openai"]}
          onChange={(value) =>
            props.onChange((current) => ({
              ...current,
              providers: {
                ...current.providers,
                llm: {
                  ...current.providers.llm,
                  provider: value as LlmProvider,
                },
              },
            }))
          }
        />
        <SelectField
          label="LLM model"
          value={spec.providers.llm.model}
          disabled={props.disabled}
          options={["gpt-4o-mini"]}
          onChange={(value) =>
            props.onChange((current) => ({
              ...current,
              providers: {
                ...current.providers,
                llm: { ...current.providers.llm, model: value },
              },
            }))
          }
        />
        <NumberField
          label="LLM temperature"
          value={spec.providers.llm.temperature}
          disabled={props.disabled}
          step="0.1"
          onChange={(value) =>
            props.onChange((current) => ({
              ...current,
              providers: {
                ...current.providers,
                llm: { ...current.providers.llm, temperature: value },
              },
            }))
          }
        />
        <SelectField
          label="TTS provider"
          value={spec.providers.tts.provider}
          disabled={props.disabled}
          options={["elevenlabs", "cartesia", "google"]}
          onChange={(value) =>
            props.onChange((current) => {
              const provider = value as TtsProvider;
              return {
                ...current,
                providers: {
                  ...current.providers,
                  tts: {
                    ...current.providers.tts,
                    provider,
                    model: defaultTtsModelForProvider(provider),
                    voice: defaultTtsVoiceForProvider(provider),
                    settings: defaultTtsSettingsForProvider(provider),
                  },
                },
              };
            })
          }
        />
        <SelectField
          label="TTS model"
          value={spec.providers.tts.model ?? ""}
          disabled={props.disabled}
          options={supportedTtsModelsForProvider(spec.providers.tts.provider)}
          onChange={(value) =>
            props.onChange((current) => {
              const provider = value.startsWith("gemini-")
                ? "google"
                : current.providers.tts.provider;
              const suggestedVoices = ttsVoiceSuggestionsForProvider(provider);
              return {
                ...current,
                providers: {
                  ...current.providers,
                  tts: {
                    ...current.providers.tts,
                    provider,
                    model: value,
                    voice:
                      suggestedVoices.length > 0 &&
                      !suggestedVoices.includes(current.providers.tts.voice)
                        ? defaultTtsVoiceForProvider(provider)
                        : current.providers.tts.voice,
                  },
                },
              };
            })
          }
        />
        <VoiceSelectField
          provider={spec.providers.tts.provider}
          value={spec.providers.tts.voice}
          disabled={props.disabled}
          onChange={(value) =>
            props.onChange((current) => ({
              ...current,
              providers: {
                ...current.providers,
                tts: { ...current.providers.tts, voice: value },
              },
            }))
          }
        />
        <ReadOnlyField
          label="TTS settings"
          value={JSON.stringify(spec.providers.tts.settings)}
        />
      </div>
      <div className="form-grid three-columns">
        <CheckboxField label="Speech start buffer" checked={firstClauseBufferSettings(spec.providers.tts.settings).enabled} disabled={props.disabled} onChange={(checked) => props.onChange((current) => updateFirstClauseBuffer(current, { enabled: checked }))} />
        <SpeechTimingSlider label="Minimum speech chunk" ariaLabel="Minimum speech chunk characters" value={firstClauseBufferSettings(spec.providers.tts.settings).min_chars} min={8} max={120} disabled={props.disabled || !firstClauseBufferSettings(spec.providers.tts.settings).enabled} fastLabel="Faster start" naturalLabel="More stable delivery" help="Minimum characters before Console lets TTS start. Lower feels snappier; higher gives the voice more context before it commits to an inflection." onChange={(value) => props.onChange((current) => updateFirstClauseBuffer(current, { min_chars: value }))} />
        <SpeechTimingSlider label="Lookahead context" ariaLabel="Lookahead context characters" value={firstClauseBufferSettings(spec.providers.tts.settings).context_chars} min={0} max={200} disabled={props.disabled || !firstClauseBufferSettings(spec.providers.tts.settings).enabled} fastLabel="Less lookahead" naturalLabel="More lookahead" help="Extra characters held as context around the first clause. More lookahead can smooth pacing, but may add a small delay." onChange={(value) => props.onChange((current) => updateFirstClauseBuffer(current, { context_chars: value }))} />
        <TextField label="Cartesia language" value={typeof spec.providers.tts.settings.language === 'string' ? spec.providers.tts.settings.language : ''} disabled={props.disabled || spec.providers.tts.provider !== 'cartesia'} onChange={(value) => props.onChange((current) => updateTtsSettings(current, { language: value.trim() || null }))} />
        <NumberField label="Cartesia speed" value={typeof spec.providers.tts.settings.speed === 'number' ? spec.providers.tts.settings.speed : 0.92} step="0.01" disabled={props.disabled || spec.providers.tts.provider !== 'cartesia'} onChange={(value) => props.onChange((current) => updateTtsSettings(current, { speed: value }))} />
        <NumberField label="Cartesia volume" value={typeof spec.providers.tts.settings.volume === 'number' ? spec.providers.tts.settings.volume : 1} step="0.1" disabled={props.disabled || spec.providers.tts.provider !== 'cartesia'} onChange={(value) => props.onChange((current) => updateTtsSettings(current, { volume: value }))} />
        <SelectField label="Cartesia emotion" value={cartesiaEmotionValue(spec.providers.tts.settings.emotion)} disabled={props.disabled || spec.providers.tts.provider !== 'cartesia'} options={CARTESIA_EMOTION_OPTIONS.map((option) => option.value)} labels={Object.fromEntries(CARTESIA_EMOTION_OPTIONS.map((option) => [option.value, option.label]))} onChange={(value) => props.onChange((current) => updateTtsSettings(current, { emotion: value || null }))} />
        <SelectField label="Cartesia sample rate" value={String(spec.providers.tts.settings.sample_rate ?? '')} disabled={props.disabled || spec.providers.tts.provider !== 'cartesia'} options={['', '8000', '16000', '24000', '44100', '48000']} labels={{ '': 'Provider default', '8000': '8000 Hz — phone narrowband', '16000': '16000 Hz — phone wideband', '24000': '24000 Hz — default voice quality', '44100': '44100 Hz', '48000': '48000 Hz' }} onChange={(value) => props.onChange((current) => updateTtsSettings(current, { sample_rate: value ? Number(value) : null }))} />
      </div>
      <p className="compact-copy muted-row">These controls write directly to <code>providers.tts.settings</code>, so the JSON blob is the runtime contract. The runtime env can only kill-switch first-clause buffering globally.</p>

      <AgentCapabilitiesSummary spec={spec} />

      <SectionTitle>Tools, routing, recording, and limits</SectionTitle>
      <div className="form-grid three-columns">
        <ReadOnlyField
          label="Tools"
          value={`${spec.tools.length} declared tools`}
        />
        <ReadOnlyField label="Routing strategy" value={spec.routing.strategy} />
        <CheckboxField
          label="Record audio"
          checked={spec.recording.record_audio}
          disabled={props.disabled}
          onChange={(checked) =>
            props.onChange((current) => ({
              ...current,
              recording: { ...current.recording, record_audio: checked },
            }))
          }
        />
        <CheckboxField
          label="Transcribe"
          checked={spec.recording.transcribe}
          disabled={props.disabled}
          onChange={(checked) =>
            props.onChange((current) => ({
              ...current,
              recording: { ...current.recording, transcribe: checked },
            }))
          }
        />
        <NumberField
          label="Max tool calls per call"
          value={spec.limits.max_tool_calls_per_call}
          disabled={props.disabled}
          onChange={(value) =>
            props.onChange((current) => ({
              ...current,
              limits: { ...current.limits, max_tool_calls_per_call: value },
            }))
          }
        />
        <NumberField
          label="Max LLM tokens per turn"
          value={spec.limits.max_llm_tokens_per_turn}
          disabled={props.disabled}
          onChange={(value) =>
            props.onChange((current) => ({
              ...current,
              limits: { ...current.limits, max_llm_tokens_per_turn: value },
            }))
          }
        />
      </div>

      <EndCallToolEditor
        spec={spec}
        disabled={props.disabled}
        onChange={props.onChange}
      />

      <CapturedFieldsEditor
        spec={spec}
        disabled={props.disabled}
        onChange={props.onChange}
      />
    </section>
  );
}

const CARTESIA_EMOTION_OPTIONS = [
  { value: "", label: "Natural / no emotion" },
  { value: "anger", label: "Angry — less presenter-like" },
  { value: "anger:high", label: "Angry high — most muted/punchy" },
  { value: "sadness", label: "Sad / subdued" },
  { value: "curiosity", label: "Curious" },
  { value: "positivity", label: "Positive" },
  { value: "surprise", label: "Surprised" },
];

const defaultFirstClauseBuffer: FirstClauseBufferSettings = {
  enabled: true,
  min_chars: 20,
  context_chars: 10,
};

function defaultTtsSettingsForProvider(provider: TtsProvider): TtsSettings {
  if (provider === "google") return {};
  return { first_clause_buffer: { ...defaultFirstClauseBuffer } };
}

function firstClauseBufferSettings(settings: TtsSettings): FirstClauseBufferSettings {
  return { ...defaultFirstClauseBuffer, ...(settings.first_clause_buffer ?? {}) };
}

function updateFirstClauseBuffer(
  spec: CallableAgentSpecV1,
  patch: Partial<FirstClauseBufferSettings>,
): CallableAgentSpecV1 {
  return updateTtsSettings(spec, {
    first_clause_buffer: {
      ...firstClauseBufferSettings(spec.providers.tts.settings),
      ...patch,
    },
  });
}

function cartesiaEmotionValue(emotion: TtsSettings["emotion"]): string {
  if (typeof emotion === "string") return emotion;
  if (Array.isArray(emotion) && typeof emotion[0] === "string") return emotion[0];
  return "";
}

function updateTtsSettings(
  spec: CallableAgentSpecV1,
  patch: Partial<TtsSettings>,
): CallableAgentSpecV1 {
  return {
    ...spec,
    providers: {
      ...spec.providers,
      tts: {
        ...spec.providers.tts,
        settings: pruneNullSettings({ ...spec.providers.tts.settings, ...patch }),
      },
    },
  };
}

function pruneNullSettings(settings: TtsSettings): TtsSettings {
  return Object.fromEntries(
    Object.entries(settings).filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    ),
  ) as TtsSettings;
}

function EndCallToolEditor(props: {
  spec: CallableAgentSpecV1;
  disabled?: boolean;
  onChange: (updater: (current: CallableAgentSpecV1) => CallableAgentSpecV1) => void;
}) {
  const config = getEndCallConfig(props.spec);
  const enabled = config !== null;
  const mode = config?.final_message_mode ?? 'natural';
  const finalMessage = config?.final_message ?? '';
  return (
    <div className="tool-config-card">
      <div className="tool-config-heading">
        <div>
          <strong>End call</strong>
          <p className="compact-copy muted-row">Optional native tool that lets the agent hang up once the conversation is complete.</p>
        </div>
        <CheckboxField
          label="Enable end call"
          checked={enabled}
          disabled={props.disabled}
          onChange={(checked) => props.onChange((current) => setEndCallConfig(current, checked ? { final_message_mode: 'natural' } : null))}
        />
      </div>
      {enabled ? (
        <div className="form-grid two-columns">
          <SelectField
            label="Ending mode"
            value={mode}
            disabled={props.disabled}
            options={['natural', 'scripted', 'silent']}
            labels={{
              natural: 'Natural — use prompt/script goodbye',
              scripted: 'Scripted — say final message first',
              silent: 'Silent — hang up without extra goodbye',
            }}
            onChange={(value) => props.onChange((current) => setEndCallConfig(current, {
              final_message_mode: value as EndCallFinalMessageMode,
              final_message: finalMessage,
            }))}
          />
          {mode === 'scripted' ? (
            <TextAreaField
              label="Final message before hangup"
              value={finalMessage}
              disabled={props.disabled}
              onChange={(value) => props.onChange((current) => setEndCallConfig(current, {
                final_message_mode: 'scripted',
                final_message: value,
              }))}
            />
          ) : (
            <ReadOnlyField
              label="Runtime behavior"
              value={mode === 'silent' ? 'Calls end without an extra goodbye.' : 'Agent says the goodbye naturally from the prompt, then hangs up.'}
            />
          )}
        </div>
      ) : null}
    </div>
  );
}

function CapturedFieldsEditor(props: {
  spec: CallableAgentSpecV1;
  disabled: boolean;
  onChange: (
    mutator: (current: CallableAgentSpecV1) => CallableAgentSpecV1,
  ) => void;
}) {
  const fields = getCaptureFields(props.spec);
  const enabled = fields.length > 0;

  function replaceFields(nextFields: CaptureFieldDefinition[]) {
    props.onChange((current) => setCaptureFields(current, nextFields));
  }

  function updateField(index: number, patch: Partial<CaptureFieldDefinition>) {
    replaceFields(
      fields.map((field, fieldIndex) =>
        fieldIndex === index ? { ...field, ...patch } : field,
      ),
    );
  }

  function addField() {
    const nextIndex = fields.length + 1;
    replaceFields([
      ...fields,
      {
        key: `captured_field_${nextIndex}`,
        label: `Captured field ${nextIndex}`,
        type: "string",
        description: "Describe when the assistant should capture this value.",
        sensitive: false,
      },
    ]);
  }

  function removeField(index: number) {
    replaceFields(fields.filter((_, fieldIndex) => fieldIndex !== index));
  }

  function moveField(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= fields.length) return;
    const next = [...fields];
    const [field] = next.splice(index, 1);
    next.splice(target, 0, field);
    replaceFields(next);
  }

  return (
    <div className="captured-fields-editor" aria-label="Captured fields editor">
      <div className="panel-title-row compact-panel-title">
        <div>
          <h3>Captured fields</h3>
          <p className="compact-copy">
            Configure the existing Agent Spec <code>capture_fields</code> tool.
            No hidden dashboard-only state is created.
          </p>
        </div>
        <CheckboxField
          label="Enable captured fields"
          checked={enabled}
          disabled={props.disabled}
          onChange={(checked) => (checked ? addField() : replaceFields([]))}
        />
      </div>
      {enabled ? (
        <div className="captured-field-list">
          {fields.map((field, index) => (
            <div className="captured-field-row" key={`${field.key}-${index}`}>
              <div className="form-grid two-columns">
                <TextField
                  label={`Field ${index + 1} label`}
                  value={field.label}
                  disabled={props.disabled}
                  onChange={(value) =>
                    updateField(index, {
                      label: value,
                      key:
                        field.key === fieldKeyFromLabel(field.label)
                          ? fieldKeyFromLabel(value)
                          : field.key,
                    })
                  }
                />
                <TextField
                  label={`Field ${index + 1} key`}
                  value={field.key}
                  disabled={props.disabled}
                  onChange={(value) => updateField(index, { key: value })}
                />
                <SelectField
                  label={`Field ${index + 1} type`}
                  value={field.type}
                  disabled={props.disabled}
                  options={[
                    "string",
                    "number",
                    "integer",
                    "boolean",
                    "string_list",
                  ]}
                  onChange={(value) =>
                    updateField(index, { type: value as CaptureFieldType })
                  }
                />
                <CheckboxField
                  label={`Field ${index + 1} sensitive`}
                  checked={Boolean(field.sensitive)}
                  disabled={props.disabled}
                  onChange={(checked) =>
                    updateField(index, { sensitive: checked })
                  }
                />
              </div>
              <TextAreaField
                label={`Field ${index + 1} description`}
                value={field.description ?? ""}
                disabled={props.disabled}
                onChange={(value) => updateField(index, { description: value })}
              />
              <div className="captured-field-actions">
                <button
                  className="secondary-button compact-action"
                  type="button"
                  disabled={props.disabled || index === 0}
                  onClick={() => moveField(index, -1)}
                >
                  Move up
                </button>
                <button
                  className="secondary-button compact-action"
                  type="button"
                  disabled={props.disabled || index === fields.length - 1}
                  onClick={() => moveField(index, 1)}
                >
                  Move down
                </button>
                <button
                  className="secondary-button compact-action danger-action"
                  type="button"
                  disabled={props.disabled}
                  onClick={() => removeField(index)}
                >
                  Delete field
                </button>
              </div>
            </div>
          ))}
          <button
            className="secondary-button"
            type="button"
            disabled={props.disabled}
            onClick={addField}
          >
            Add captured field
          </button>
        </div>
      ) : (
        <p className="compact-copy muted-row">
          No captured fields configured. Enable this to add the native{" "}
          <code>capture_fields</code> tool to the Agent Spec.
        </p>
      )}
    </div>
  );
}

function JsonSpecEditor(props: {
  jsonText: string;
  jsonError: string | null;
  validation: ValidationResult;
  onJsonChange: (value: string) => void;
  onValidate: () => void;
  disabled: boolean;
}) {
  return (
    <section
      className="panel builder-panel json-panel"
      aria-labelledby="json-editor-heading"
    >
      <div className="panel-title-row">
        <h2 id="json-editor-heading">
          <Code2 size={16} aria-hidden="true" /> JSON editor
        </h2>
        <button
          className="secondary-button"
          type="button"
          onClick={props.onValidate}
        >
          Validate
        </button>
      </div>
      <textarea
        className="json-editor mono"
        aria-label="Agent Spec JSON"
        spellCheck={false}
        value={props.jsonText}
        onChange={(event) => props.onJsonChange(event.target.value)}
        disabled={props.disabled}
      />
      <ValidationSummary
        jsonError={props.jsonError}
        validation={props.validation}
      />
    </section>
  );
}

function SpecContractShell(props: {
  currentAgent: ApiAgentWithVersions | null;
  editingSpec: CallableAgentSpecV1;
  onLoadVersion: (version: ApiAgentWithVersions["versions"][number]) => void;
  onPublishVersion: (version: ApiAgentWithVersions["versions"][number]) => void;
  publishingVersionId: string | null;
  operationPending: boolean;
}) {
  const { currentAgent } = props;
  const [selectedDiffVersionId, setSelectedDiffVersionId] = useState<
    string | null
  >(null);
  const [diffFeedback, setDiffFeedback] = useState<string | null>(null);
  const sortedVersions = useMemo(
    () =>
      currentAgent
        ? [...currentAgent.versions].sort(
            (a, b) => b.version_number - a.version_number,
          )
        : [],
    [currentAgent],
  );
  const diffVersion =
    sortedVersions.find((version) => version.id === selectedDiffVersionId) ??
    sortedVersions[0];
  const diffRows = diffVersion
    ? diffAgentSpecs(
        diffVersion.spec_json,
        props.editingSpec as unknown as Record<string, unknown>,
      )
    : [];

  function selectDiffVersion(
    version: ApiAgentWithVersions["versions"][number],
  ) {
    setSelectedDiffVersionId(version.id);
    setDiffFeedback(
      `Showing diff for v${version.version_number} against the current editor draft.`,
    );
  }

  return (
    <section
      className="panel builder-panel context-panel"
      aria-labelledby="spec-contract-heading"
    >
      <div className="panel-title-row">
        <h2 id="spec-contract-heading">
          <Braces size={16} aria-hidden="true" /> Agent versions & spec contract
        </h2>
        <span className="table-label">Diff + rollback</span>
      </div>
      {currentAgent && (
        <div className="version-summary">
          <h3>Versions</h3>
          <div className="version-list">
            {sortedVersions.length === 0 && (
              <p className="compact-copy muted-row">
                No versions yet. Save a draft to create the first version.
              </p>
            )}
            {sortedVersions.map((version) => {
              const isActive =
                version.id === currentAgent.agent.active_version_id;
              const canPublish = version.status !== "archived" && !isActive;
              const disabledReason = getVersionPublishDisabledReason({
                isActive,
                isPublishing: props.publishingVersionId === version.id,
                status: version.status,
              });
              const reasonId = `version-${version.id}-publish-reason`;
              return (
                <div
                  key={version.id}
                  className={`version-row ${selectedDiffVersionId === version.id ? "diff-selected" : ""}`}
                >
                  <strong>v{version.version_number}</strong>
                  <span
                    className={`status-badge ${isActive ? "active" : version.status}`}
                  >
                    {isActive ? "active" : version.status}
                  </span>
                  <div className="version-meta">
                    {version.published_at && (
                      <small>
                        Published{" "}
                        {new Date(version.published_at).toLocaleDateString()}
                      </small>
                    )}
                    {!version.published_at && <small>Draft</small>}
                    {disabledReason ? (
                      <small id={reasonId} className="version-disabled-reason">
                        {disabledReason}
                      </small>
                    ) : null}
                  </div>
                  <div className="version-actions">
                    <button
                      className="secondary-button compact-action"
                      type="button"
                      onClick={() => props.onLoadVersion(version)}
                      disabled={props.operationPending}
                    >
                      <FileText size={13} aria-hidden="true" /> Edit from v
                      {version.version_number}
                    </button>
                    <button
                      className="secondary-button compact-action"
                      type="button"
                      onClick={() => selectDiffVersion(version)}
                    >
                      <GitCompare size={13} aria-hidden="true" /> Compare
                    </button>
                    <button
                      className="secondary-button compact-action"
                      type="button"
                      title={disabledReason ?? undefined}
                      aria-describedby={disabledReason ? reasonId : undefined}
                      disabled={
                        !canPublish || props.operationPending
                      }
                      onClick={() => props.onPublishVersion(version)}
                    >
                      <RotateCcw size={13} aria-hidden="true" />{" "}
                      {version.status === "draft" ? "Publish" : "Rollback"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          {diffFeedback ? (
            <p className="compact-copy compare-feedback" role="status">
              {diffFeedback}
            </p>
          ) : null}
          {diffVersion ? (
            <VersionDiffPanel
              versionNumber={diffVersion.version_number}
              rows={diffRows}
            />
          ) : null}
        </div>
      )}
      <p className="compact-copy">
        Context variables, trigger config, and richer tool audit fields are now
        part of Agent Spec v1 JSON. This shell validates and round-trips them
        without creating dashboard-only state; dedicated visual editors remain a
        follow-up.
      </p>
      <div className="context-grid">
        <SpecContractColumn
          title="Context rendering"
          rows={[
            ["Declared variables", "context.inputs / collections"],
            ["Sample context", "context.sample_context"],
          ]}
        />
        <SpecContractColumn
          title="Trigger config"
          rows={[
            ["Inbound DID trigger", "triggers[]"],
            ["Browser test call", "triggers[]"],
          ]}
        />
        <SpecContractColumn
          title="Tool auditability"
          rows={[
            ["MCP connector permissions", "tools[]"],
            ["Timeout/retry policy", "tools[].timeout_ms / max_attempts"],
          ]}
        />
      </div>
    </section>
  );
}

function getVersionPublishDisabledReason(props: {
  isActive: boolean;
  isPublishing: boolean;
  status: ApiAgentWithVersions["versions"][number]["status"];
}): string | null {
  if (props.isPublishing) return "Publishing this version now.";
  if (props.status === "archived")
    return "Archived versions cannot be published or rolled back.";
  if (props.isActive) return "This is already the active published version.";
  return null;
}

function VersionDiffPanel(props: {
  versionNumber: number;
  rows: ReturnType<typeof diffAgentSpecs>;
}) {
  return (
    <div
      className="version-diff"
      aria-label={`Version ${props.versionNumber} diff`}
    >
      <h3>Diff v{props.versionNumber} → editor draft</h3>
      {props.rows.length === 0 ? (
        <p className="compact-copy muted-row">
          No JSON changes between this version and the current editor draft.
        </p>
      ) : (
        <ul>
          {props.rows.slice(0, 12).map((row) => (
            <li key={row.path}>
              <strong>{row.path}</strong>
              <span>{row.before}</span>
              <span>{row.after}</span>
            </li>
          ))}
        </ul>
      )}
      {props.rows.length > 12 ? (
        <p className="compact-copy muted-row">
          {props.rows.length - 12} more changes hidden; use the JSON editor for
          full detail.
        </p>
      ) : null}
    </div>
  );
}

function SpecContractColumn(props: {
  title: string;
  rows: [string, string][];
}) {
  return (
    <div className="context-column">
      <h3>
        <Lock size={13} aria-hidden="true" /> {props.title}
      </h3>
      {props.rows.map(([name, type]) => (
        <div className="context-row muted-row" key={name}>
          <FileText size={14} aria-hidden="true" />
          <span>{name}</span>
          <small>{type}</small>
        </div>
      ))}
    </div>
  );
}

function ValidationSummary(props: {
  jsonError: string | null;
  validation: ValidationResult;
}) {
  const valid = !props.jsonError && props.validation.valid;
  return (
    <div className={`validation-summary ${valid ? "valid" : "invalid"}`}>
      {valid ? (
        <CheckCircle2 size={16} aria-hidden="true" />
      ) : (
        <AlertTriangle size={16} aria-hidden="true" />
      )}
      <div>
        <strong>
          {valid ? "Locally valid Agent Spec shape" : "Needs attention"}
        </strong>
        <ul>
          {props.validation.messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function SectionTitle(props: { children: string }) {
  return <div className="form-section-title">{props.children}</div>;
}

function TextField(props: {
  label: string;
  value: string;
  disabled?: boolean;
  suggestions?: string[];
  hint?: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  if (!props.hint && !props.suggestions?.length) {
    return (
      <label className="field-label">
        {props.label}
        <input
          disabled={props.disabled}
          value={props.value}
          onChange={(event) => props.onChange(event.target.value)}
        />
      </label>
    );
  }
  const listId = props.suggestions?.length ? `${id}-suggestions` : undefined;
  const hintId = props.hint ? `${id}-hint` : undefined;
  return (
    <div className="field-label">
      <label htmlFor={id}>{props.label}</label>
      <input
        id={id}
        disabled={props.disabled}
        value={props.value}
        list={listId}
        aria-describedby={hintId}
        onChange={(event) => props.onChange(event.target.value)}
      />
      {listId ? (
        <datalist id={listId}>
          {props.suggestions?.map((option) => (
            <option value={option} key={option} />
          ))}
        </datalist>
      ) : null}
      {props.hint ? (
        <small id={hintId} className="field-hint">
          {props.hint}
        </small>
      ) : null}
    </div>
  );
}

function TextAreaField(props: {
  label: string;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <label className="field-label full-width">
      {props.label}
      <textarea
        value={props.value}
        disabled={props.disabled}
        onChange={(event) => props.onChange(event.target.value)}
      />
    </label>
  );
}

function AmbienceVolumeField(props: {
  value: number;
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  const value = Math.max(-45, Math.min(-6, props.value));
  const description = value >= -12 ? "Prominent" : value >= -18 ? "Audible" : "Subtle";
  return (
    <label className="field-label ambience-volume-field">
      <span>Ambience volume</span>
      <input
        aria-label="Ambience volume dB"
        type="range"
        min="-45"
        max="-6"
        step="1"
        disabled={props.disabled}
        value={value}
        onChange={(event) => props.onChange(Number(event.target.value))}
      />
      <span className="field-help ambience-volume-readout">
        {value} dB · {description}
      </span>
      <input
        aria-label="Ambience volume dB numeric"
        type="number"
        min="-45"
        max="-6"
        step="1"
        disabled={props.disabled}
        value={value}
        onChange={(event) => props.onChange(Number(event.target.value))}
      />
    </label>
  );
}

function SpeechTimingSlider(props: {
  label: string;
  ariaLabel: string;
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
  fastLabel: string;
  naturalLabel: string;
  help: string;
  onChange: (value: number) => void;
}) {
  const value = Math.max(props.min, Math.min(props.max, props.value));
  return (
    <label className="field-label speech-timing-field">
      <span>{props.label}</span>
      <input
        aria-label={props.ariaLabel}
        type="range"
        min={props.min}
        max={props.max}
        step="1"
        disabled={props.disabled}
        value={value}
        onChange={(event) => props.onChange(Number(event.target.value))}
      />
      <span className="speech-timing-scale" aria-hidden="true">
        <span>{props.fastLabel}</span>
        <span>{props.naturalLabel}</span>
      </span>
      <span className="field-help speech-timing-readout">
        {value} chars · range {props.min}–{props.max}
      </span>
      <span className="field-help">{props.help}</span>
    </label>
  );
}

function NumberField(props: {
  label: string;
  value: number;
  step?: string;
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <label className="field-label">
      {props.label}
      <input
        type="number"
        step={props.step ?? "1"}
        disabled={props.disabled}
        value={props.value}
        onChange={(event) => props.onChange(Number(event.target.value))}
      />
    </label>
  );
}

function SelectField(props: {
  label: string;
  value: string;
  options: string[];
  labels?: Record<string, string>;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const current = props.options.includes(props.value) ? null : (
    <option value={props.value} disabled>
      {props.value ? `${props.value} (not supported, choose another)` : "Choose one"}
    </option>
  );
  return (
    <label className="field-label">
      {props.label}
      <select
        aria-label={props.label}
        disabled={props.disabled}
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
      >
        {current}
        {props.options.map((option) => (
          <option value={option} key={option}>
            {props.labels?.[option] ?? option}
          </option>
        ))}
      </select>
    </label>
  );
}

const fallbackVoices: Record<
  TtsProvider,
  Array<{ value: string; label: string }>
> = {
  elevenlabs: [
    { value: "rachel", label: "Rachel — ElevenLabs fallback" },
    { value: "antoni", label: "Antoni — ElevenLabs fallback" },
    { value: "Rachel", label: "Rachel — ElevenLabs legacy" },
    { value: "Antoni", label: "Antoni — ElevenLabs legacy" },
  ],
  cartesia: [
    {
      value: "9510a875-60dc-4c7a-97b2-3fa64d753916",
      label: "Australian custom voice — Cartesia fallback",
    },
  ],
  google: [
    { value: "Kore", label: "Kore — Gemini fallback" },
    { value: "Puck", label: "Puck — Gemini fallback" },
  ],
  openai: [
    { value: "alloy", label: "Alloy — OpenAI" },
    { value: "verse", label: "Verse — OpenAI" },
  ],
  custom: [],
};

type CatalogTtsProvider = "elevenlabs" | "cartesia";

function isCatalogTtsProvider(provider: TtsProvider): provider is CatalogTtsProvider {
  return provider === "elevenlabs" || provider === "cartesia";
}

function VoiceSelectField(props: {
  provider: TtsProvider;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const [voices, setVoices] = useState<ApiTtsVoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const catalogProvider = isCatalogTtsProvider(props.provider)
    ? props.provider
    : null;
  const canUseCatalog = catalogProvider !== null;

  useEffect(() => {
    if (!canUseCatalog) return;
    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      setLoading(true);
      fetchTtsVoices(catalogProvider)
        .then((loaded) => {
          if (!cancelled) setVoices(loaded);
        })
        .catch((error) => {
          if (!cancelled)
            setMessage(
              error instanceof Error
                ? error.message
                : "Could not load synced voices.",
            );
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [canUseCatalog, catalogProvider]);

  async function handleSync() {
    if (!canUseCatalog) return;
    try {
      setSyncing(true);
      setMessage(null);
      if (catalogProvider === null) return;
      const result = await syncTtsVoices(catalogProvider);
      setVoices(result.voices);
      setMessage(`Synced ${result.synced_count} voice(s).`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Voice sync failed.");
    } finally {
      setSyncing(false);
    }
  }

  const catalogOptions = voices
    .filter((voice) => voice.provider === props.provider && voice.status === "available")
    .map((voice) => ({
      value: voice.provider_voice_id,
      label: `${voice.display_name} — ${voice.provider}`,
    }));
  const configured =
    catalogOptions.length > 0
      ? catalogOptions
      : (fallbackVoices[props.provider] ?? []);
  const providerDefaultVoice = defaultTtsVoiceForProvider(props.provider);
  const hasConfiguredValue = configured.some((voice) => voice.value === props.value);
  const suggestedVoiceOptions = ttsVoiceSuggestionsForProvider(props.provider).map((voice) => ({
    value: voice,
    label: `${voice} — ${props.provider === "google" ? "Gemini" : props.provider} fallback`,
  }));
  const preserveUnknownVoice =
    props.value && configured.length === 0 && suggestedVoiceOptions.length === 0;
  const options = hasConfiguredValue
    ? configured
    : preserveUnknownVoice
      ? [{ value: props.value, label: `${props.value} — current unknown voice` }]
      : configured.length > 0
        ? configured
        : suggestedVoiceOptions;
  const selectedValue = hasConfiguredValue || preserveUnknownVoice
    ? props.value
    : providerDefaultVoice || options[0]?.value || "";

  return (
    <label className="field-label">
      TTS voice
      <select
        aria-label="TTS voice"
        disabled={props.disabled}
        value={selectedValue}
        onChange={(event) => props.onChange(event.target.value || selectedValue)}
      >
        {options.map((voice) => (
          <option value={voice.value} key={voice.value}>
            {voice.label}
          </option>
        ))}
      </select>
      <span className="field-help">
        {loading
          ? "Loading synced voices…"
          : catalogOptions.length > 0
            ? "Synced from Voices."
            : "Fallback list shown until provider voices are synced."}
      </span>
      {canUseCatalog ? (
        <button
          className="secondary-button compact-action"
          type="button"
          disabled={props.disabled || syncing}
          onClick={() => void handleSync()}
        >
          {syncing ? "Syncing…" : "Sync voices"}
        </button>
      ) : null}
      {message ? <span className="field-help">{message}</span> : null}
    </label>
  );
}

function AgentCapabilitiesSummary(props: { spec: CallableAgentSpecV1 }) {
  const memoryEnabled = hasContactMemoryContext(props.spec);
  const dispositionTool = props.spec.tools.find(
    (tool) => tool.kind === "disposition_call",
  );
  const rawDispositions = isRecord(dispositionTool?.config)
    ? dispositionTool.config.available_dispositions
    : undefined;
  const dispositions = Array.isArray(rawDispositions)
    ? rawDispositions
        .map((item) => {
          if (typeof item === "string") return item;
          if (!isRecord(item)) return null;
          const label = typeof item.label === "string" ? item.label : null;
          const key = typeof item.key === "string" ? item.key : null;
          return label ?? key;
        })
        .filter((item): item is string => Boolean(item))
    : [];
  const dispositionEnabled = Boolean(dispositionTool);

  return (
    <section
      className="agent-capabilities-summary"
      aria-label="Agent capabilities"
    >
      <div className="capability-card">
        <strong>Memory</strong>
        <span className={`status-pill ${memoryEnabled ? "synced" : "unknown"}`}>
          <i /> {memoryEnabled ? "Enabled" : "Not declared"}
        </span>
        <p>
          {memoryEnabled
            ? "Contact-memory variables are declared in this Agent Spec."
            : "Declare memory variables before expecting prior contact context."}
        </p>
      </div>
      <div className="capability-card">
        <strong>Dispositions</strong>
        <span
          className={`status-pill ${dispositionEnabled ? "synced" : "unknown"}`}
        >
          <i /> {dispositionEnabled ? "Enabled" : "Not declared"}
        </span>
        {dispositions.length > 0 ? (
          <ul className="capability-chip-list">
            {dispositions.map((disposition) => (
              <li key={disposition}>{disposition}</li>
            ))}
          </ul>
        ) : (
          <p>No disposition options are configured for this agent.</p>
        )}
      </div>
    </section>
  );
}

function CheckboxField(props: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="checkbox-field">
      <input
        type="checkbox"
        disabled={props.disabled}
        checked={props.checked}
        onChange={(event) => props.onChange(event.target.checked)}
      />{" "}
      {props.label}
    </label>
  );
}

function ReadOnlyField(props: { label: string; value: string }) {
  return (
    <label className="field-label">
      {props.label}
      <input readOnly value={props.value} />
    </label>
  );
}
